import { createAppSlice } from "../../app/createAppSlice"
import { createEntityAdapter, isAnyOf } from '@reduxjs/toolkit'
import { startAppListening } from "../../app/listenerMiddleware"
import { changedResourceAdded } from "../changedResourcesList/changedResourcesListSlice"
import addFoisFromFlisInTargetResources from "../../resourceComponents/compositionResourceFunctions/addFoisFromFlisInTargetResources"
import { selectCurrentUserApiToken } from "../currentUser/currentUserSlice"

const loadOutcomeVariablesCollection = async (findingsSection,
    apiToken, combinedDictionary = {}) => {
    let outcomeVariablesCollection = [];
    let outcomeVariablesResources = [];
    let fliListToProcess = [];
    outcomeVariablesResources = findingsSection.map(section => {
        let entry;
        if (section.focus?.reference) {
            let sourcefoi = section.focus.reference?.replace("EvidenceVariable/", "") || "missing";
            let sourcetitle = section.focus.display || "";
            if (!isNaN(sourcefoi)) {
                entry = {
                    id: sourcefoi,
                    title: sourcetitle,
                    shortTitle: sourcetitle.replace("OutcomeDefinition: ", "")
                        .replace("VariableDefinition: ", "")
                        .replace("DIO: ", "")
                        .replace("DichotomousIntendedOutcome: ", "")
                };
            } else if (section.focus.identifier?.system === "https://fevir.net/FLI" && section.focus.identifier.value) {
                let sourcefli = section.focus.identifier.value;
                entry = {
                    id: NaN,
                    title: sourcetitle,
                    shortTitle: sourcetitle.replace("OutcomeDefinition: ", "")
                        .replace("VariableDefinition: ", "")
                        .replace("DIO: ", "")
                        .replace("DichotomousIntendedOutcome: ", ""),
                    fli: sourcefli
                };
                fliListToProcess.push(sourcefli);
            }
        }
        return entry;
    });

    if (fliListToProcess.length > 0) {
        await addFoisFromFlisInTargetResources(outcomeVariablesResources, apiToken);
    }
    if (outcomeVariablesResources.length > 0) {
        outcomeVariablesCollection = outcomeVariablesResources.map(entry => {
            if (!entry) { //Some entries were undefined
                return {};
            }
            return {
                id: entry.id,
                title: entry.title,
                shortTitle: combinedDictionary[entry.id]?.shortTitle || entry.shortTitle,
                fli: entry.fli,
                resource: combinedDictionary[entry.id], //JSON.parse(JSON.stringify(combinedDictionary[entry.id]))
                reference: {
                    reference: "EvidenceVariable/" + entry.id,
                    type: "EvidenceVariable",
                    display: combinedDictionary[entry.id]?.title || entry.title
                }
            }
        });
    }
    return outcomeVariablesCollection;
};

//entity: {  id: entry.id,
//          title: entry.title,
//          shortTitle: combinedDictionary[entry.id]?.shortTitle || entry.shortTitle,
//          fli: entry.fli,
//          resource: combinedDictionary[entry.id],
//          reference: {
//              reference: "EvidenceVariable/" + entry.id,
//              type: "EvidenceVariable",
//              display: combinedDictionary[entry.id]?.title || entry.title
//          }
// }
// 
//key uses: customized Summary of Findings and Summary of Net Effect, Change Baseline Risks

const outcomeVariablesAdapter = createEntityAdapter({
    sortComparer: (b, a) => {
        if (b?.shortTitle && a?.shortTitle && b.shortTitle !== a.shortTitle) {
            return b.shortTitle.localeCompare(a.shortTitle)
        } else if (b?.id && a?.id && b.id !== a.id) {
            return b.id.localeCompare(a.id)
        } else {
            return 0
        }
    }
})

const initialState = outcomeVariablesAdapter.getInitialState({
    status: 'idle',
    error: null
})

const outcomeVariablesSlice = createAppSlice({
    name: "outcomeVariables",
    initialState,
    reducers: create => ({
        outcomeVariableAdded: create.preparedReducer(
            (outcomeVariablesBody) => {
                return {
                    payload: {
                        id: outcomeVariablesBody.id,
                        title: outcomeVariablesBody.title,
                        shortTitle: outcomeVariablesBody.shortTitle,
                        fli: outcomeVariablesBody.fli,
                        resource: outcomeVariablesBody.resource,
                        reference: outcomeVariablesBody.reference
                    }
                };
            },
            outcomeVariablesAdapter.addOne
        ),
        outcomeVariableUpdated: create.reducer((state, action) => {
            const { id, title, shortTitle, fli, resource, reference } = action.payload;
            outcomeVariablesAdapter.updateOne(state,
                {
                    id, changes: {
                        title, shortTitle, fli, resource, reference
                    }
                })
        }),
        fetchOutcomeVariables: create.asyncThunk(
            async (body, { getState }) => {
                const state = getState();
                const apiToken = selectCurrentUserApiToken(state);
                const resourceEntities = await loadOutcomeVariablesCollection(body.findingsSection, apiToken, body.combinedDictionary);
                return resourceEntities
            },
            {
                /*
                 options: {
                     condition(arg, thunkApi) {
                         const { outcomeVariables } = thunkApi.getState()
                         if (outcomeVariables.status !== 'idle') {
                             return false
                         }
                     }
                 },
                 */
                pending: state => {
                    state.status = "loading"
                },
                fulfilled: (state, action) => {
                    state.status = "succeeded"
                    outcomeVariablesAdapter.setAll(state, action.payload)
                },
                rejected: (state, action) => {
                    state.status = "failed"
                    state.error = action.error.message ?? "Unknown Error"
                },
            },
        ),
    }),
    selectors: {
        selectOutcomeVariablesStatus: outcomeVariables => outcomeVariables.status,
        selectOutcomeVariablesError: outcomeVariables => outcomeVariables.error
    },
})

export const { outcomeVariableAdded, outcomeVariableUpdated, fetchOutcomeVariables } = outcomeVariablesSlice.actions

export const { selectOutcomeVariablesStatus, selectOutcomeVariablesError } = outcomeVariablesSlice.selectors

export const {
    selectAll: selectAllOutcomeVariables,
    selectById: selectOutcomeVariableById,
} = outcomeVariablesAdapter.getSelectors(state => state.outcomeVariables)

export const addOutcomeVariablesListeners = () => {

    startAppListening({
        matcher: isAnyOf(outcomeVariableAdded, outcomeVariableUpdated),
        effect: (action, listenerApi) => {
            try {
                const resourceChange = action.payload;
                listenerApi.dispatch(changedResourceAdded({ singleUpdate: resourceChange.id }));
            } catch (err) {
                console.log("FAILURE of outcomeVariables listener: ", err)
            }
        }
    })
}

export default outcomeVariablesSlice